import {
  InlineComment,
  QualityCheckStatus,
  WorkflowEventTypes,
  WorkflowState,
} from '@amzn/awscat-pattern-library-service-model';
import PQueue from 'p-queue';

import { IMplServiceClient } from '../api/IMplServiceClient';
import { log } from '../loggers/Logger';
import { isCommentResolved } from '../components/v2-common/utils/commentStateUtils';
import { ContentEditorService } from './ContentEditorService';
import { ServiceCallError } from './Errors';
import { InlineCommentManager } from './InlineCommentManager';
import { InlineEditsManager, OpenedEdits } from './InlineEditsManager';
import { applyInlineEditsToContent } from './InlineEditApplier';
import { PollingTimer } from './PollingTimer';
import { isScannerComment } from './ScannerComments';
import { TypedEmitter, Unsubscribe } from './TypedEmitter';

/** The scan lifecycle state. */
export type ScanState = 'idle' | 'scan_in_progress' | 'loading_results';

/**
 * Thrown when the backend rejects a scan request because the author has
 * submitted too many scans. The backend currently allows one scan every 60
 * seconds and 20 scans per rolling 24 hours per content; see the MPL service
 * `RescanRateLimit` for the source of truth.
 */
export class TooManyScansError extends Error {
  constructor(readonly cause: ServiceCallError) {
    super('You are submitting too many scans. Please wait a minute and try again.');
    this.name = 'TooManyScansError';
  }
}

/** How the scan concluded. */
export type ScanCompletionStatus = 'completed' | 'failed' | 'cancelled' | 'timed_out';

/** Results fetched after a scan concludes. */
export interface QualityCheckScanResults {
  readonly status: ScanCompletionStatus;
  readonly scanId?: string;
  readonly scanComments: readonly InlineComment[] | undefined;
  readonly scanEdits: OpenedEdits | undefined;
}

/** Events emitted by QualityCheckService. */
export type QualityCheckEvents = {
  changed: () => void;
  allChangesAccepted: () => void;
};

/**
 * Shows a fatal error to the user. The caller decides how (flashbar, alert,
 * modal, …) and what recovery affordance (reload, refresh) to attach.
 */
export type DisplayFatalError = (message: string) => void;

/**
 * Timeout duration for the scan in milliseconds (17 minutes).
 *
 * The quality check runs in a Lambda whose max execution time is 15 minutes.
 * We wait two extra minutes for network and workflow overhead before giving up.
 */
const SCAN_TIMEOUT_MS = 17 * 60 * 1000;

/** Polling interval for checking workflow scan status (5 seconds). */
const POLL_INTERVAL_MS = 5_000;

/** Max time to wait for the workflow to acknowledge the event (30 seconds). */
const CONFIRMATION_TIMEOUT_MS = 30_000;

/**
 * QualityCheckService manages the lifecycle of content quality scans.
 *
 * All public mutations are serialized via PQueue to prevent interleaving.
 *
 * Flow:
 * 1. Author clicks "Start quality check" → service posts START_QUALITY_CHECK workflow event
 * 2. Service polls getContentWorkflow() checking qualityCheckInfo.scanStatus
 * 3. When scanStatus === COMPLETE → fetches inline edits and scan comments
 * 4. When scanStatus === FAILED → transitions to idle with errors
 */
export class QualityCheckService {
  private readonly _emitter = new TypedEmitter<QualityCheckEvents>();
  private readonly _mpl: IMplServiceClient;
  private readonly _contentEditorService: ContentEditorService;
  private readonly _commentManager: InlineCommentManager;
  private readonly _editsManager: InlineEditsManager;
  private readonly _displayFatalError: DisplayFatalError;
  private readonly _queue = new PQueue({ concurrency: 1 });
  private readonly _timer = new PollingTimer(POLL_INTERVAL_MS, SCAN_TIMEOUT_MS);
  private readonly _unsubComments: Unsubscribe;
  private readonly _unsubEdits: Unsubscribe;
  private _state: ScanState = 'idle';
  private _results: QualityCheckScanResults | null = null;
  /** Bumped on every reset; async work discards itself once its captured value goes stale. */
  private _generation = 0;
  private _scanConfirmed = false;
  private _refreshing = false;
  private _pollStartTime = 0;
  private _contentId: string | null = null;
  private _workflowId: number | null = null;
  private _scanId: string | undefined = undefined;

  constructor(
    mpl: IMplServiceClient,
    contentEditorService: ContentEditorService,
    commentManager: InlineCommentManager,
    editsManager: InlineEditsManager,
    displayFatalError: DisplayFatalError,
  ) {
    this._mpl = mpl;
    this._contentEditorService = contentEditorService;
    this._commentManager = commentManager;
    this._editsManager = editsManager;
    this._displayFatalError = displayFatalError;

    this._unsubComments = commentManager.on('changed', () => this.handleExternalFindingsChange());
    this._unsubEdits = editsManager.on('changed', () => this.handleExternalFindingsChange());
  }

  get state(): ScanState {
    return this._state;
  }

  get results(): QualityCheckScanResults | null {
    return this._results;
  }

  /**
   * Whether the backend has started running the scan.
   *
   * When the user starts a scan, the request is queued on the backend and this
   * service flips to `scan_in_progress` right away. This property stays `false`
   * during that queue wait and flips to `true` once polling sees the backend
   * actually running the scan.
   */
  get isScanConfirmed(): boolean {
    return this._scanConfirmed;
  }

  get openedEdits(): OpenedEdits | undefined {
    return this._editsManager.openedEdits;
  }

  /**
   * Load existing scan state on page load. Kicks off a single poll cycle
   * which will handle whatever state the workflow is in (COMPLETE, FAILED,
   * CANCELLED, or IN_PROGRESS).
   */
  loadExistingResults(contentId: string, workflowId: number): void {
    // Captured now, not when the operation dequeues: a reset while this waits in
    // the queue must discard it rather than let it adopt the new generation.
    const generation = this._generation;
    void this._queue.add(() => this.doLoadExistingResults(contentId, workflowId, generation));
  }

  /**
   * Start a quality check scan. No-op if already scanning.
   * Posts a START_QUALITY_CHECK workflow event and begins polling for completion.
   *
   * @throws {Error} when the previous scan timed out, because it may still be
   * running server-side; callers must disable their rescan controls in that state
   * rather than calling this
   * @throws {ContentValidationError} when saving content fails before starting
   * the quality check
   * @throws {SaveWorkingCopyError} when the intermediate working-copy save fails
   * @throws {TooManyScansError} when the backend responds 429 to the start
   * event, signalling the author must wait before scanning again
   * @throws {ServiceCallError} when the backend call to start the scan fails
   * for any other reason
   */
  async start(contentId: string, workflowId: number): Promise<void> {
    const generation = this._generation;
    await this._queue.add(() => this.doStart(contentId, workflowId, generation));
  }

  /**
   * Cancel an in-flight scan by posting a CANCEL_QUALITY_CHECK event.
   * Polling stays live and drives the transition to idle once the workflow
   * reports CANCELLED.
   */
  async cancel(): Promise<void> {
    const generation = this._generation;
    await this._queue.add(() => this.doCancel(generation));
  }

  /** Clear stored results and emit a change. No-op unless idle. */
  async clearResults(): Promise<void> {
    await this._queue.add(() => {
      if (this._state !== 'idle') return;
      this._results = null;
      this._emitter.emit('changed');
    });
  }

  /** Drop all scan state and abandon in-flight polling. Call right before loading a content. */
  resetState(): void {
    this._generation++;
    this._timer.stop();
    this._state = 'idle';
    this._results = null;
    this._scanId = undefined;
    this._scanConfirmed = false;
    this._pollStartTime = 0;
    this._contentId = null;
    this._workflowId = null;
    this._emitter.emit('changed');
  }

  /**
   * Submit the content for publisher review, then refresh the workflow.
   *
   * @throws {ServiceCallError} when the backend call fails.
   */
  async submitForPublisherReview(contentId: string, workflowId: number): Promise<void> {
    await this._mpl.postWorkflowEvent(
      contentId,
      workflowId,
      WorkflowEventTypes.SUBMIT_FOR_PUBLISHER_REVIEW,
      '',
      undefined,
      undefined,
      undefined,
      { suppressSuccessFlashbar: true },
    );
    await this._contentEditorService.refreshWorkflow();
  }

  /**
   * Bulk-resolve all unresolved inline edits and comments, then refresh results.
   *
   * @throws {ServiceCallError} when the backend call fails.
   */
  async acceptAllChanges(): Promise<void> {
    const generation = this._generation;
    await this._queue.add(() => this.doAcceptAllChanges(generation));
  }

  /**
   * Re-fetch inline edits and comments without polling the workflow.
   * Use after local mutations (e.g., bulk-resolve) to refresh displayed findings.
   */
  async refreshResults(): Promise<void> {
    const generation = this._generation;
    await this._queue.add(() => this.doRefreshResults(generation));
  }

  on<K extends keyof QualityCheckEvents>(event: K, listener: QualityCheckEvents[K]): Unsubscribe {
    return this._emitter.on(event, listener);
  }

  /**
   * Release all resources. Call when the service is no longer needed
   * (e.g., provider unmount) to prevent leaked timers.
   */
  close(): void {
    this._unsubComments();
    this._unsubEdits();

    // Bumping the generation neutralizes queued and in-flight work, so the queue
    // is left alone — clearing it would leave callers awaiting `add()` forever.
    this.resetState();
  }

  // ─── Private: queued operations ────────────────────────────────────────

  private doLoadExistingResults(contentId: string, workflowId: number, generation: number): void {
    if (generation !== this._generation) return;

    if (this._state === 'scan_in_progress' || this._state === 'loading_results') {
      return;
    }

    this._state = 'loading_results';
    this._scanConfirmed = true;
    this._pollStartTime = Date.now();
    this._contentId = contentId;
    this._workflowId = workflowId;
    this._emitter.emit('changed');

    this._timer.start(
      () => void this._queue.add(() => this.doPoll(contentId, generation, null, undefined)),
      () => this.handleTimeout(generation),
    );
  }

  /**
   * @throws {Error} when the previous scan timed out and may still be running
   * server-side
   * @throws {ContentValidationError} when saving content fails before starting
   * the quality check
   * @throws {SaveWorkingCopyError} when the intermediate working-copy save fails
   */
  private async doStart(contentId: string, workflowId: number, generation: number): Promise<void> {
    if (generation !== this._generation) return;

    if (this._state === 'scan_in_progress' || this._state === 'loading_results') {
      return;
    }

    // A timed-out scan leaves the state idle, so the state check above cannot catch it.
    // That scan may still be running server-side, and a second one would race it.
    if (this._results?.status === 'timed_out') {
      throw new Error('Cannot start a quality check while the previous scan is timed out');
    }

    // Persist any unsaved edits so the scanner sees the latest content.
    await this._contentEditorService.save();

    // The content may have been unloaded while saving; starting a scan it can no
    // longer track would leave the workflow scanning with nobody watching.
    if (generation !== this._generation) {
      return;
    }

    // Snapshot the previous scan's results and scanId so we can restore them
    // if the start event fails — the new scan never began, so wiping the prior
    // findings (or the id a later refresh would stamp onto them) would
    // misrepresent the state.
    const previousResults = this._results;
    const previousScanId = this._scanId;
    this._state = 'scan_in_progress';
    this._results = null;
    this._scanConfirmed = false;
    this._scanId = undefined;
    this._pollStartTime = Date.now();
    this._contentId = contentId;
    this._workflowId = workflowId;
    this._emitter.emit('changed');

    try {
      await this._mpl.postWorkflowEvent(
        contentId,
        workflowId,
        WorkflowEventTypes.START_QUALITY_CHECK,
        '',
        undefined,
        undefined,
        undefined,
        { suppressSuccessFlashbar: true },
      );
    } catch (error) {
      log.error({ error, contentId }, 'Failed to post START_QUALITY_CHECK event');
      // The scan never started, so reset to idle and restore the previous
      // scan's state. A 'failed' status would misrepresent this as a scan that
      // ran and then failed.
      this._scanId = previousScanId;
      this.transitionToIdle(generation, previousResults);

      // Re-throw throttling errors as TooManyScansError,
      // so we don't leak the implementation details.
      if (error instanceof ServiceCallError && error.isThrottlingError) {
        throw new TooManyScansError(error);
      }
      throw error;
    }

    if (generation !== this._generation) {
      return;
    }

    this._timer.start(
      () =>
        void this._queue.add(() =>
          this.doPoll(contentId, generation, previousResults, previousScanId),
        ),
      () => this.handleTimeout(generation),
    );
  }

  private async doCancel(generation: number): Promise<void> {
    // A cancel queued for the previously open content must not cancel this one's scan.
    if (generation !== this._generation) return;

    if (this._state !== 'scan_in_progress') {
      return;
    }

    const contentId = this._contentId;
    const workflowId = this._workflowId;
    if (!contentId || !workflowId) return;

    // Post the event and leave polling running; the CANCELLED status will drive
    // the transition to idle via handleScanCancelled.
    try {
      await this._mpl.postWorkflowEvent(
        contentId,
        workflowId,
        WorkflowEventTypes.CANCEL_QUALITY_CHECK,
        '',
        undefined,
        undefined,
        undefined,
        { suppressSuccessFlashbar: true },
      );
    } catch (error) {
      log.error({ error, contentId }, 'Failed to post CANCEL_QUALITY_CHECK event');
    }
  }

  /** @throws {ServiceCallError} when the backend call fails. */
  private async doAcceptAllChanges(generation: number): Promise<void> {
    if (generation !== this._generation) return;

    const unresolvedCommentIds = (this._results?.scanComments ?? [])
      .filter((c) => !isCommentResolved(c.state))
      .map((c) => c.commentId);

    // Take a copy of unresolved inline edits for later use
    const unresolvedEdits = this._editsManager.openedEdits?.edits.filter((e) => !e.resolved) ?? [];

    // Resolve server-side first — if this throws, no local mutation happens.
    await Promise.all([
      this._editsManager.bulkResolve(),
      this._commentManager.resolveAllScanComments(unresolvedCommentIds),
    ]);

    // The user may have opened another item while the server calls ran; staging
    // now would write these edits into the wrong document.
    if (generation !== this._generation) return;

    // Both server calls succeeded; Now apply inline edits.
    const currentContent = this._contentEditorService.pullContent();
    if (!currentContent) return;
    this._contentEditorService.stageEdit(
      applyInlineEditsToContent(currentContent, unresolvedEdits),
    );
    this._emitter.emit('allChangesAccepted');

    // Pass the captured generation, not the current one: a reset during the
    // bulk-resolve above must not let the refresh publish 'completed' results
    // against whatever content was opened in the meantime.
    await this.doRefreshResults(generation);
  }

  private async doRefreshResults(generation: number, skipCommentRefresh = false): Promise<void> {
    this._refreshing = true;
    try {
      if (!skipCommentRefresh) {
        await this._commentManager.refresh();
      }
      if (generation !== this._generation) return;

      const allComments = this._commentManager.openedComments?.comments ?? [];
      const scanComments = allComments.filter(isScannerComment);

      this._results = {
        status: this._results?.status ?? 'completed',
        scanId: this._scanId,
        scanComments,
        scanEdits: this._editsManager.openedEdits,
      };
      this._emitter.emit('changed');
    } catch (error) {
      log.error({ error, contentId: this._contentId }, 'Failed to refresh scan results');
    } finally {
      this._refreshing = false;
    }
  }

  // ─── Private: polling ──────────────────────────────────────────────────

  private async doPoll(
    contentId: string,
    generation: number,
    previousResults: QualityCheckScanResults | null,
    previousScanId: string | undefined,
  ): Promise<void> {
    if (!this.isActive(generation)) return;

    try {
      // Refresh the workflow, but do it through the ContentEditorService
      // (instead of mpl). This is so views display the workflow state will get
      // updated automatically.
      await this._contentEditorService.refreshWorkflow();

      // The content may have been unloaded while the workflow was in flight, in which
      // case the handle below belongs to another content (or to nothing at all).
      if (!this.isActive(generation)) return;

      const reloadedDocument = this._contentEditorService.loadedDocumentHandle?.document;

      // Type guard - this should never happen:
      if (!reloadedDocument || reloadedDocument.kind !== 'draft') {
        throw new Error('A document in draft state is expected');
      }

      const workflow = reloadedDocument.workflow;
      const scanStatus = workflow?.qualityCheckInfo?.scanStatus;

      const wasScanConfirmed = this._scanConfirmed;
      if (
        scanStatus === QualityCheckStatus.IN_PROGRESS ||
        workflow?.draftState === WorkflowState.QUALITY_CHECK
      ) {
        this._scanConfirmed = true;
        this._scanId = workflow?.qualityCheckInfo?.scanId;
      }

      if (this._state === 'loading_results' && scanStatus === QualityCheckStatus.IN_PROGRESS) {
        this._state = 'scan_in_progress';
        this._emitter.emit('changed');
      } else if (!wasScanConfirmed && this._scanConfirmed) {
        // Un-gate the Cancel button now that the backend has acknowledged the scan.
        this._emitter.emit('changed');
      }

      if (!this._scanConfirmed) {
        if (Date.now() - this._pollStartTime >= CONFIRMATION_TIMEOUT_MS) {
          log.error({ contentId }, 'Workflow never transitioned to IN_PROGRESS');
          // The scan never started on the server, so restore the prior scan's
          // findings instead of marking this as failed.
          this._scanId = previousScanId;
          this.transitionToIdle(generation, previousResults);
          return;
        }
      } else if (scanStatus === QualityCheckStatus.COMPLETE) {
        await this.handleScanComplete(contentId, generation);
        return;
      } else if (scanStatus === QualityCheckStatus.FAILED) {
        this.transitionToIdle(generation, {
          status: 'failed',
          scanId: this._scanId,
          scanComments: undefined,
          scanEdits: undefined,
        });
        return;
      } else if (scanStatus === QualityCheckStatus.CANCELLED) {
        await this.handleScanCancelled(contentId, workflow?.draftState, generation);
        return;
      }
    } catch (error) {
      log.error({ error, contentId }, 'Failed to poll workflow for quality check status');
    }

    if (this.isActive(generation)) {
      this._timer.scheduleNext();
    }
  }

  // ─── Private: terminal handlers ────────────────────────────────────────

  private async handleScanCancelled(
    contentId: string,
    draftState: WorkflowState | undefined,
    generation: number,
  ): Promise<void> {
    if (!this.isActive(generation)) return;

    if (draftState !== WorkflowState.QUALITY_CHECK) {
      this.transitionToIdle(generation, {
        status: 'cancelled',
        scanComments: undefined,
        scanEdits: undefined,
      });
      return;
    }

    try {
      await Promise.all([
        this._contentEditorService.refreshWorkflow(),
        this._editsManager.open(contentId),
        this._commentManager.refresh(),
      ]);

      // The content may have been unloaded or switched during the fetches above;
      // writing _scanId then would tag the next content's results with this scan.
      if (generation !== this._generation) return;

      // After cancel, the backend restores previousScanId into scanId.
      // Update _scanId so it reflects the restored prior scan.
      const reloadedDoc = this._contentEditorService.loadedDocumentHandle?.document;
      if (reloadedDoc?.kind === 'draft') {
        this._scanId = reloadedDoc.workflow.qualityCheckInfo?.scanId;
      }

      const allComments = this._commentManager.openedComments?.comments ?? [];
      const scanComments = allComments.filter(isScannerComment);

      this.transitionToIdle(generation, {
        status: 'cancelled',
        scanId: this._scanId,
        scanComments,
        scanEdits: this._editsManager.openedEdits,
      });
    } catch (error) {
      log.error({ error, contentId }, 'Failed to fetch restored findings after cancel');
      this.transitionToIdle(generation, {
        status: 'cancelled',
        scanComments: undefined,
        scanEdits: undefined,
      });
    }
  }

  private async handleScanComplete(contentId: string, generation: number): Promise<void> {
    if (!this.isActive(generation)) return;

    try {
      await Promise.all([
        this._contentEditorService.refreshWorkflow(),
        this._editsManager.open(contentId),
        this._commentManager.refresh(),
      ]);

      const allComments = this._commentManager.openedComments?.comments ?? [];
      const scanComments = allComments.filter(isScannerComment);

      this.transitionToIdle(generation, {
        status: 'completed',
        scanId: this._scanId,
        scanComments,
        scanEdits: this._editsManager.openedEdits,
      });
    } catch (error) {
      log.error({ error, contentId }, 'Failed to fetch scan results after completion');

      // Skip if the content was unloaded or switched mid-fetch (stale generation).
      if (!this.isActive(generation)) return;

      // The scan finished on the server, but we could not load its results here.
      // Leaving the app running would be very awkward: the user would see an
      // idle editor with no findings even though the scan produced some.
      // It's best to stop the application by reporting a fatal error here.
      this._timer.stop();
      this._displayFatalError(
        'The scan has finished, but we could not load the scan results. Please reload the web page to try again.',
      );
    }
  }

  // ─── Private: helpers ──────────────────────────────────────────────────

  private isActive(generation: number): boolean {
    return (
      generation === this._generation &&
      (this._state === 'scan_in_progress' || this._state === 'loading_results')
    );
  }

  private handleTimeout(generation: number): void {
    if (!this.isActive(generation)) return;

    this.transitionToIdle(generation, {
      status: 'timed_out',
      scanComments: undefined,
      scanEdits: undefined,
    });
  }

  private handleExternalFindingsChange(): void {
    if (this._refreshing || this._state !== 'idle') return;

    // Emit so consumers of openedEdits get updates.
    this._emitter.emit('changed');

    // Skip refresh when the comment manager has nothing open — the 'changed'
    // event also fires on close() during teardown (e.g. navigating away after
    // submit), and refreshing then would throw NoContentOpenError.
    if (this._results && this._contentId && this._commentManager.openedComments) {
      const generation = this._generation;
      void this._queue.add(() => this.doRefreshResults(generation, true));
    }
  }

  private transitionToIdle(generation: number, results: QualityCheckScanResults | null): void {
    if (generation !== this._generation) return;

    this._timer.stop();
    this._state = 'idle';
    this._results = results;
    this._emitter.emit('changed');
  }
}
